<template>
	<a-modal
		class="g-quick-nav-grid-edit-modal"
		:visible="visible"
		title="3列跳转编辑"
		width="730px"
		:centered="true"
		:mask-closable="false"
		:destroy-on-close="true"
		@cancel="onCancel"
	>
		<div class="g-edit-modal__scroll">
			<div class="g-quick-nav-grid-edit" @click="clearSelection">
				<div class="g-quick-nav-grid-edit__actions" @click.stop>
					<button type="button" class="g-quick-nav-grid-edit__batch-btn" @click="addBatch">添加一批</button>
					<button type="button" class="g-quick-nav-grid-edit__batch-btn" @click="addOne">添加一格</button>
				</div>

				<EditListSortGroup
					ref="sortGroup"
					class="g-quick-nav-grid-edit__list"
					@dragover="onSortDragOver"
				>
					<div
						v-for="(item, index) in form.items"
						:key="item.id"
						:data-sort-id="item.id"
						class="g-quick-nav-grid-edit__cell"
						:class="[
							{ 'is-active': selectedIndex === index },
							getEditListSortItemClass(dragId, item.id, dragOverId),
						]"
						:draggable="form.items.length > 1"
						@click.stop
						@dragstart="onItemDragStart(item.id)"
						@dragend="onItemDragEnd"
					>
						<div
							class="g-quick-nav-grid-edit__row"
							:class="{ 'is-selected': selectedIndex === index }"
							@click="selectItem(index)"
						>
							<div class="g-quick-nav-grid-edit__icon">
								<UploadProgressPanel
									v-if="isUploading && uploadTargetIndex === index"
									:tasks="uploadTasks"
									variant="item"
									hide-filename
									:status-text="''"
								/>
								<img v-else-if="getImageUrl(item)" :src="getImageUrl(item)" alt="" />
								<i v-else class="mbm-iconfont mbm-tupian"></i>
							</div>
							<input
								v-model="item.text"
								type="text"
								class="g-quick-nav-grid-edit__text-input"
								:style="{ color: item.textColor }"
								maxlength="20"
								placeholder="按钮文字"
								@click.stop="selectItem(index)"
								@focus="selectItem(index)"
							/>
						</div>

						<div
							v-if="selectedIndex === index"
							class="g-quick-nav-edit__panel g-quick-nav-edit__panel--arrow g-quick-nav-edit__panel--float"
							:class="getPanelAlignClass(index)"
							@click.stop
						>
							<div class="g-quick-nav-edit__link-row">
								<i class="mbm-iconfont mbm-lianjie"></i>
								<span :class="{ 'is-empty': !getJumpLinkLabel(item) }">
									{{ getJumpLinkLabel(item) || '未设置链接' }}
								</span>
							</div>
							<div class="g-quick-nav-edit__toolbar">
								<button type="button" class="g-quick-nav-edit__action" @click="triggerImageSelect">换图</button>
								<button type="button" class="g-quick-nav-edit__action" @click="jumpLinkVisible = true">换链接</button>
								<button
									type="button"
									class="g-quick-nav-edit__action g-quick-nav-edit__action--icon"
									title="在下方添加"
									@click="insertAfter(index)"
								>
									<i class="mbm-iconfont mbm-jia"></i>
								</button>
								<button
									type="button"
									class="g-quick-nav-edit__action g-quick-nav-edit__action--danger-text"
									:disabled="form.items.length <= 1"
									@click="deleteItem(index)"
								>删除</button>
								<div class="g-quick-nav-edit__colors">
									<ColorPicker
										v-model="item.textColor"
										title="文字颜色"
										@change="val => onColorInput('textColor', val, index)"
									/>
								</div>
							</div>
						</div>
					</div>
				</EditListSortGroup>
			</div>
		</div>

		<template #footer>
			<a-button @click="onCancel">取消</a-button>
			<a-button type="primary" class="g-quick-nav-edit-modal__ok" @click="onConfirm">确定</a-button>
		</template>

		<JumpLink
			:visible="jumpLinkVisible"
			:jumpLink="selectedItem?.jumpLink || emptyJumpLink"
			:linkTypes="['qrcode']"
			@submit="onJumpLinkSubmit"
			@cancel="jumpLinkVisible = false"
		/>

		<input
			ref="imageInput"
			type="file"
			accept="image/*"
			class="g-quick-nav-edit__file-input"
			@change="onImageSelect"
		/>
	</a-modal>
</template>

<script>
	import JumpLink from '@/components/jumpLink.vue';
	import ColorPicker from './ColorPicker.vue';
	import { createEmptyJumpLink, formatJumpLinkLabel } from '../utils/jumpLink.js';
	import {
		isLocalImageFile,
		MAX_LOCAL_IMAGE_SIZE,
		revokeLocalImageUrl,
	} from '../utils/localImage.js';
	import {
		createDefaultQuickNavItem,
		createDefaultQuickNavItems,
		getQuickNavImageUrl,
		normalizeQuickNavGridData,
		replaceQuickNavImage,
	} from '../utils/quickNav.js';
	import { ensureEditFloatPanelVisible, getGridPanelAlignClass } from '../utils/editPanelAlign.js';
	import { uploadHeaderMediaFiles, createUploadTasks, updateUploadTaskProgress } from '../utils/uploadHeaderMedia.js';
	import {
		createEditListDragState,
		getEditListSortItemClass,
		handleEditListSortDragEnd,
		handleEditListSortDragOver,
		handleEditListSortDragStart,
		resolveSelectedIndexAfterListMove,
	} from '../utils/editListDragSort.js';
	import EditListSortGroup from './EditListSortGroup.vue';
	import UploadProgressPanel from './UploadProgressPanel.vue';

	const COL_COUNT = 3;

	export default {
		name: 'GQuickNavGridEditModal',
		components: { EditListSortGroup, JumpLink, ColorPicker, UploadProgressPanel },
		props: {
			visible: {
				type: Boolean,
				default: false,
			},
			data: {
				type: Object,
				default: () => ({}),
			},
		},
		emits: ['update:visible', 'confirm'],
		data() {
			return {
				form: normalizeQuickNavGridData({}),
				selectedIndex: null,
				jumpLinkVisible: false,
				emptyJumpLink: createEmptyJumpLink(),
				isUploading: false,
				uploadTargetIndex: null,
				uploadTasks: [],
				...createEditListDragState(),
			};
		},
		computed: {
			selectedItem() {
				return this.form.items[this.selectedIndex] || null;
			},
		},
		watch: {
			visible(val) {
				if (val) {
					this.selectedIndex = null;
					this.syncForm();
				}
			},
			data: {
				deep: true,
				handler() {
					if (this.visible) {
						this.syncForm();
					}
				},
			},
		},
		methods: {
			getEditListSortItemClass,
			getPanelAlignClass(index) {
				return getGridPanelAlignClass(index, COL_COUNT);
			},
			ensureActivePanelVisible() {
				ensureEditFloatPanelVisible(this, {
					wrapSelector: '.g-quick-nav-grid-edit__cell',
					panelSelector: '.g-quick-nav-edit__panel--float',
					gridRef: 'sortGroup',
				});
			},
			getImageUrl(item) {
				return getQuickNavImageUrl(item?.image);
			},
			getJumpLinkLabel(item) {
				return formatJumpLinkLabel(item?.jumpLink);
			},
			syncForm() {
				this.form = normalizeQuickNavGridData(this.data);
				if (
					this.selectedIndex != null
					&& this.selectedIndex >= this.form.items.length
				) {
					this.selectedIndex = null;
				}
			},
			selectItem(index) {
				this.selectedIndex = index;
				this.ensureActivePanelVisible();
			},
			clearSelection() {
				this.selectedIndex = null;
			},
			onItemDragStart(id) {
				handleEditListSortDragStart(this, id);
			},
			onSortDragOver(event) {
				handleEditListSortDragOver(event, this.$refs.sortGroup, this);
			},
			onItemDragEnd() {
				handleEditListSortDragEnd(this, this.$refs.sortGroup, this.form.items, this, result => {
					this.selectedIndex = resolveSelectedIndexAfterListMove(
						this.selectedIndex,
						result.fromIndex,
						result.toIndex,
					);
				});
			},
			addBatch() {
				this.form.items.push(...createDefaultQuickNavItems(3));
				this.selectedIndex = this.form.items.length - 3;
			},
			addOne() {
				this.form.items.push(createDefaultQuickNavItem({}, this.form.items.length));
				this.selectedIndex = this.form.items.length - 1;
			},
			insertAfter(index) {
				this.form.items.splice(index + 1, 0, createDefaultQuickNavItem({}, index + 1));
				this.selectedIndex = index + 1;
			},
			deleteItem(index) {
				if (this.form.items.length <= 1) return;
				const removed = this.form.items[index];
				if (removed?.image?.local) {
					revokeLocalImageUrl(removed.image.previewUrl);
				}
				this.form.items.splice(index, 1);
				if (this.selectedIndex === index) {
					this.selectedIndex = null;
				} else if (this.selectedIndex != null && this.selectedIndex > index) {
					this.selectedIndex -= 1;
				}
			},
			triggerImageSelect() {
				this.$refs.imageInput?.click();
			},
			async onImageSelect(event) {
				const file = event.target.files?.[0];
				event.target.value = '';
				if (!file || !isLocalImageFile(file) || !this.selectedItem) return;
				if (file.size > MAX_LOCAL_IMAGE_SIZE) return;
				const targetIndex = this.selectedIndex;
				this.uploadTargetIndex = targetIndex;
				this.uploadTasks = createUploadTasks([file]);
				this.isUploading = true;
				try {
					const [item] = await uploadHeaderMediaFiles(this, {
						type: 'image',
						files: [file],
						onProgress: payload => updateUploadTaskProgress(this.uploadTasks, payload),
					});
					if (targetIndex === this.selectedIndex) {
						this.form.items[targetIndex].image = replaceQuickNavImage(
							this.form.items[targetIndex].image,
							file,
							() => item,
						);
					}
				} catch (error) {
					this.$message?.error?.('上传失败');
				} finally {
					this.isUploading = false;
					this.uploadTargetIndex = null;
					this.uploadTasks = [];
				}
			},
			onColorInput(field, color, index) {
				if (this.form.items[index]) {
					this.form.items[index][field] = color;
				}
			},
			onJumpLinkSubmit(link) {
				if (this.selectedItem) {
					this.selectedItem.jumpLink = { ...link };
				}
				this.jumpLinkVisible = false;
			},
			onCancel() {
				this.$emit('update:visible', false);
			},
			onConfirm() {
				const payload = normalizeQuickNavGridData({
					items: this.form.items.map(item => ({
						...item,
						text: item.text?.trim() || '功能入口',
						jumpLink: item.jumpLink?.type ? { ...item.jumpLink } : null,
					})),
				});
				this.$emit('confirm', payload);
				this.$emit('update:visible', false);
			},
		},
	};
</script>

<style scoped lang="less">
	@import '../styles/edit-float-panel.less';

	.edit-float-panel(g-quick-nav-edit__panel);
	.edit-float-toolbar(g-quick-nav-edit__toolbar);
	.edit-float-action(g-quick-nav-edit__action);

	.g-quick-nav-grid-edit {
		min-height: 360px;
		padding: 4px 0 12px;
	}

	.g-quick-nav-grid-edit__actions {
		display: flex;
		justify-content: flex-end;
		gap: 16px;
		margin-bottom: 16px;
	}

	.g-quick-nav-grid-edit__batch-btn {
		border: none;
		background: transparent;
		padding: 0;
		font-size: 13px;
		color: #285af9;
		cursor: pointer;

		&:hover {
			color: #1d4ed8;
		}
	}

	.g-quick-nav-grid-edit__list {
		max-width: 520px;
		margin: 0 auto;
		display: grid;
		grid-template-columns: repeat(3, 1fr);
		gap: 12px 8px;
		overflow: visible;
	}

	.g-quick-nav-grid-edit__cell {
		position: relative;
		z-index: 0;
		overflow: visible;
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 8px;
		min-width: 0;

		&.is-active {
			z-index: 2;
		}

		&.is-list-sort-dragging {
			opacity: 0.4;
		}
	}

	.g-quick-nav-grid-edit__row {
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 8px;
		padding: 12px 4px;
		min-height: 72px;
		box-sizing: border-box;
		cursor: pointer;
		width: 100%;
		min-width: 0;
		border: 1px solid transparent;
		border-radius: 2px;

		&.is-selected {
			border: 1px dashed #285af9;
		}
	}

	.g-quick-nav-grid-edit__icon {
		flex-shrink: 0;
		width: 40px;
		height: 40px;
		border-radius: 4px;
		display: flex;
		align-items: center;
		justify-content: center;
		overflow: hidden;
		background: #f0f0f0;

		img {
			width: 100%;
			height: 100%;
			object-fit: contain;
			display: block;
		}

		i {
			color: #bfbfbf;
			font-size: 18px;
		}
	}

	.g-quick-nav-grid-edit__text-input {
		width: 100%;
		border: none;
		background: transparent;
		outline: none;
		font-size: 12px;
		line-height: 1.4;
		text-align: center;
		font-family: inherit;
		padding: 0;
		min-width: 0;

		&::placeholder {
			color: #bfbfbf;
		}
	}

	.g-quick-nav-edit__link-row {
		display: flex;
		align-items: center;
		gap: 8px;
		min-height: 40px;
		padding: 0 14px;
		border-bottom: 1px solid #f0f0f0;
		font-size: 13px;
		color: #333;

		i {
			color: #999;
			font-size: 14px;
		}

		.is-empty {
			color: #999;
		}
	}

	.g-quick-nav-edit__action {
		&--icon i {
			font-size: 14px;
		}
	}

	.g-quick-nav-edit__colors {
		display: inline-flex;
		align-items: center;
		gap: 6px;
		margin-left: auto;
		padding-left: 8px;
	}

	.g-quick-nav-edit__file-input {
		display: none;
	}
</style>
